import {
  View,
  Text,
  TextInput,
  ActivityIndicator,
  Linking
} from 'react-native'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { globalStyles } from '../../../static/styles/global'
import HomeDetailsHeader from '../../home/HomeDetailsHeader'
import { useTranslation } from 'react-i18next'
import Card from '../../shared/card/Card'
import CustomButton from '../../shared/button/Button'
import { Formik } from 'formik'
import * as yup from 'yup'
import { useFormsStore } from '../../../stores/onboarding'
import { useInitializeStore } from '../../../stores/initialize'
import SelectKeyValue from '../../shared/form/SelectKeyValue'
import { mapSelectValue } from '../../../utils'
import { usePersonalStore } from '../../../stores/personalInfo'
import MaskInput, { Masks } from 'react-native-mask-input'
import moment from 'moment'
import Toast from 'react-native-toast-message'
import AsyncStorage from '@react-native-async-storage/async-storage'

const I9InformationForm = ({ navigation, route }: any): JSX.Element => {
  const { params } = route
  const {
    mostRecentUserOnBoardings: userId,
    mode,
    essUserOnBoardingKey,
    essUserOnBoardingFormKey
  } = params

  const { t } = useTranslation()
  const options3 = [
    { key: 1, value: t('A citizen of the United States') },
    { key: 2, value: t('A noncitizen national of the United States') },
    { key: 3, value: t('A lawful permanent resident') },
    {
      key: 4,
      value: t(
        'A noncitizen (other than Item Numbers 2. and 3. above) authorized to work until'
      )
    }
  ]

  const intialValues = {
    citizenship: '',
    expirydate: '',
    uscisNumber: '',
    i94Number: '',
    passportNumber: '',
    passportCountry: ''
  }
  const w4schema = () => {
    return yup.object({
      citizenship: yup.string().required(t('Citizenship is a required field')),

      uscisNumber: yup.string().when('citizenship', {
        is: (val: number) => val == 3 || val == 4,
        then: (schema) =>
          schema
            .matches(/^\d*$/, t('USCIS A-Number must be numeric')) // only digits
            .max(9, t('USCIS A-Number must be at most 9 digits'))
            .test(
              'uscis-required',
              '',
              function (value) {
                const { i94Number, passportNumber, citizenship } = this.parent

                if (citizenship == 3) {
                  if (!value) {
                    return this.createError({ message: t('USCIS A-Number is required') })
                  }
                  return true
                }

                if (!value && !i94Number && !passportNumber) {
                  return this.createError({
                    message: t(
                      'At least one of USCIS A-Number, Form I-94 Admission Number, or Foreign Passport Number is required'
                    )
                  })
                }
                return true
              }
            )
      }),

      i94Number: yup.string().when('citizenship', {
        is: (val: number) => val == 4,
        then: (schema) =>
          schema
            .matches(/^\d*$/, t('Form I-94 must be numeric')) // only digits
            .test(
              'i94-length',
              t('Form I-94 must be exactly 11 digits'),
              function (value) {
                if (!value) return true // allow empty (handled by required logic)
                return value.length === 11
              }
            )
            .test(
              'i94-required',
              t(
                'At least one of USCIS A-Number, Form I-94 Admission Number, or Foreign Passport Number is required'
              ),
              function (value) {
                const { uscisNumber, passportNumber } = this.parent

                if (!value && !uscisNumber && !passportNumber) {
                  return false
                }
                return true
              }
            )
      }),

      passportNumber: yup.string().when('citizenship', {
        is: (val: number) => val == 4,
        then: (schema) =>
          schema
            .matches(
              /^[a-zA-Z0-9]*$/,
              t('Passport number must be alphanumeric')
            )
            .max(9, t('Passport number must be at most 9 characters'))
            .test(
              'passport-required',
              t(
                'At least one of USCIS A-Number, Form I-94 Admission Number, or Foreign Passport Number is required'
              ),
              function (value) {
                const { uscisNumber, i94Number } = this.parent

                if (!value && !uscisNumber && !i94Number) {
                  return false
                }

                return true
              }
            )
      }),

      passportCountry: yup.string().when('passportNumber', {
        is: (val: any) => val && val.length > 0,
        then: (schema) => schema.required(t('Country of issuance is required')),
        otherwise: (schema) => schema.notRequired()
      }),

      expirydate: yup
        .string()
        .nullable()
        .test(
          'future-date-or-empty',
          t('Expiry date must be a future date or empty'),
          function (value) {
            if (!value) return true // allow empty

            const inputDate = new Date(value)

            // Invalid date check
            if (isNaN(inputDate.getTime())) return false

            const today = new Date()
            today.setHours(0, 0, 0, 0)

            return inputDate > today
          }
        )
    })
  }

  const schema = w4schema()
  const { posti9details, loading } = useFormsStore()
  const initializestore = useInitializeStore()
  const personalstore = usePersonalStore()

  const getLanguage = async () => {
    try {
      const language = (await AsyncStorage.getItem('selectedLanguage')) || 'ENG'
      if (language === 'SPA') {
        return 'Spanish'
      } else {
        return 'English'
      }
    } catch (error) {
      console.error('Error retrieving selected language:', error)
      return 'English'
    }
  }

  return (
    <KeyboardAwareScrollView
      enableOnAndroid={true}
      enableAutomaticScroll={true}
      enableResetScrollToCoords={false}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="interactive"
      showsVerticalScrollIndicator={false}
      extraScrollHeight={80}
      contentContainerStyle={{ flexGrow: 1, paddingBottom: 20 }}
    >
      <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
        <View style={[globalStyles.horizontalPadGap]}>
          <HomeDetailsHeader
            navLabel={t('Forms')}
            navScreen="OnboardingForms"
            title={t('I-9 Information')}
          />
        </View>
        <View style={{ padding: 5, margin: 5 }}>
          <Text style={{ textAlign: 'center' }}>
            <Text style={[globalStyles.whiteTextColor]}>
              {t('View Instructions Here')} :
            </Text>{' '}
            <Text
              style={[
                {
                  fontWeight: 'bold',
                  textDecorationLine: 'underline',
                  color: 'lightblue'
                }
              ]}
              onPress={() =>
                Linking.openURL(
                  'https://www.uscis.gov/sites/default/files/document/forms/i-9instr.pdf'
                )
              }
            >
              https://www.uscis.gov/sites/default/files/document/forms/i-9instr.pdf
            </Text>
          </Text>
        </View>
      </View>
      <View style={globalStyles.containerOverlap}>
        <Formik
          initialValues={{ ...intialValues }}
          validationSchema={schema}
          onSubmit={async (values, actions) => {
            const data = {
              essUserKey: initializestore.initializeData?.userkey,
              formKey: essUserOnBoardingFormKey,
              essObKey: essUserOnBoardingKey,
              entityId: personalstore.personalData?.entityId,
              citizenship: values?.citizenship?.toString(),
              aNumber: values?.uscisNumber ? "A" + values?.uscisNumber : "",
              i94Number: values?.i94Number,
              passportNumber: values?.passportNumber,
              passportCountry: values?.passportCountry,
              expiryDate: values?.expirydate
                ? moment(values.expirydate).format('YYYY-MM-DD')
                : null,
              completeOnline: mode === 'upload' ? false : true,
              language: await getLanguage()
            }
            const response: any = await posti9details(data)
            if (response?.success) {
              if (mode === 'upload') {
                await Toast.show({
                  type: 'success',
                  position: 'bottom',
                  text1: response?.message,
                  visibilityTime: 3000,
                  autoHide: true,
                  topOffset: 60,
                  onHide: () => {
                    navigation.replace('OnboardingForms')
                  }
                })
              } else {
                navigation.navigate('I9informationonline', {
                  userId,
                  mode,
                  essUserOnBoardingKey,
                  essUserOnBoardingFormKey,
                  values
                })
              }
            } else {
              await Toast.show({
                type: 'error',
                position: 'bottom',
                text1: response?.message,
                visibilityTime: 3000,
                autoHide: true,
                topOffset: 60
              })
            }
          }}
        >
          {(props) => (
            <>
              <Card>
                <View
                  style={[
                    globalStyles.hDirection,
                    globalStyles.hBetweenItems,
                    globalStyles.mb2
                  ]}
                >
                  <View style={{ justifyContent: 'center', width: '48%' }}>
                    <Text
                      style={[
                        globalStyles.inputLabel,
                        globalStyles.inputLabelLarge,
                        globalStyles.inputLabelPrimary
                      ]}
                    >
                      {t('Citizenship')}*
                    </Text>
                  </View>
                  <View style={{ width: '48%', overflow: 'hidden' }}>
                    <SelectKeyValue
                      title={t('Citizenship')}
                      options={options3}
                      selectedOption={mapSelectValue(
                        options3,
                        props.values.citizenship
                      )}
                      onSelect={(option: number) => {
                        if (option != 4 && option != 3) {
                          props.setFieldValue('uscisNumber', '')
                        }
                        if (option != 4) {
                          props.setFieldValue('expirydate', '')
                          props.setFieldValue('i94Number', '')
                          props.setFieldValue('passportNumber', '')
                          props.setFieldValue('passportCountry', '')
                        }
                        props.setFieldValue('citizenship', option)
                      }}
                      textColor="primary"
                    />
                    {props.touched.citizenship && props.errors.citizenship && (
                      <Text style={globalStyles.dangerAlert}>
                        {props.errors.citizenship as string}
                      </Text>
                    )}
                  </View>
                </View>
                {Number(props.values.citizenship) == 4 && (
                  <View
                    style={[
                      globalStyles.hDirection,
                      globalStyles.hBetweenItems,
                      globalStyles.mb2
                    ]}
                  >
                    <View style={{ justifyContent: 'center', width: '48%' }}>
                      <Text
                        style={[
                          globalStyles.inputLabel,
                          globalStyles.inputLabelLarge,
                          globalStyles.inputLabelPrimary
                        ]}
                      >
                        {t('Expiry date ( if any)')}
                      </Text>
                    </View>
                    <View style={{ width: '48%' }}>
                      <MaskInput
                        style={[globalStyles.input, globalStyles.inputLight]}
                        value={props.values.expirydate || ''}
                        onChangeText={props.handleChange('expirydate')}
                        mask={Masks.DATE_MMDDYYYY}
                        inputMode="numeric"
                        placeholder="MM/DD/YYYY"
                      />
                      {props.touched.expirydate &&
                        props.errors.expirydate && (
                          <Text style={globalStyles.dangerAlert}>
                            {props.errors.expirydate as string}
                          </Text>
                        )}
                    </View>
                  </View>
                )}

                {(Number(props.values.citizenship) == 3 || Number(props.values.citizenship) == 4) && (
                  <View
                    style={[
                      globalStyles.hDirection,
                      globalStyles.hBetweenItems,
                      globalStyles.mb2
                    ]}
                  >
                    <View style={{ justifyContent: 'center', width: '48%' }}>
                      <Text
                        style={[
                          globalStyles.inputLabel,
                          globalStyles.inputLabelLarge,
                          globalStyles.inputLabelPrimary
                        ]}
                      >
                        {t('USCIS A-Number ')}
                        {Number(props.values.citizenship) == 3 ? '*' : ''}
                      </Text>
                    </View>
                    <View style={{ width: '48%' }}>
                      <TextInput
                        style={[globalStyles.input, globalStyles.inputLight]}
                        value={props.values.uscisNumber}
                        onChangeText={(text) => {
                          if (text === '' || /^\d+$/.test(text)) {
                            props.handleChange('uscisNumber')(text)
                          }
                        }}
                      />
                      {props.touched.uscisNumber &&
                        props.errors.uscisNumber && (
                          <Text style={[globalStyles.dangerAlert]}>
                            {props.errors.uscisNumber as string}
                          </Text>
                        )}
                    </View>
                  </View>
                )}

                {Number(props.values.citizenship) == 4 && (
                  <>
                    <View
                      style={[
                        globalStyles.hDirection,
                        globalStyles.hBetweenItems,
                        globalStyles.mb2
                      ]}
                    >
                      <View style={{ justifyContent: 'center', width: '48%' }}>
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary
                          ]}
                        >
                          {t('Form I-94 Admission Number')}
                        </Text>
                      </View>
                      <View style={{ width: '48%' }}>
                        <TextInput
                          style={[globalStyles.input, globalStyles.inputLight]}
                          value={props.values.i94Number}
                          onChangeText={(text) => {
                            if (text === '' || /^\d+$/.test(text)) {
                              props.handleChange('i94Number')(text)
                            }
                          }}
                        />
                        {props.touched.i94Number && props.errors.i94Number && (
                          <Text style={[globalStyles.dangerAlert]}>
                            {props.errors.i94Number as string}
                          </Text>
                        )}
                      </View>
                    </View>

                    <View
                      style={[
                        globalStyles.hDirection,
                        globalStyles.hBetweenItems,
                        globalStyles.mb2
                      ]}
                    >
                      <View style={{ justifyContent: 'center', width: '48%' }}>
                        <Text
                          style={[
                            globalStyles.inputLabel,
                            globalStyles.inputLabelLarge,
                            globalStyles.inputLabelPrimary
                          ]}
                        >
                          {t('Foreign Passport Number')}
                        </Text>
                      </View>
                      <View style={{ width: '48%' }}>
                        <TextInput
                          style={[globalStyles.input, globalStyles.inputLight]}
                          value={props.values.passportNumber}
                          onChangeText={(text) => {
                            props.setFieldValue('passportNumber', text)
                            if (!text) {
                              props.setFieldValue('passportCountry', '')
                            }
                          }}
                        />
                        {props.touched.passportNumber &&
                          props.errors.passportNumber && (
                            <Text style={[globalStyles.dangerAlert]}>
                              {props.errors.passportNumber as string}
                            </Text>
                          )}
                      </View>
                    </View>

                    {!!props.values.passportNumber && (
                      <View
                        style={[
                          globalStyles.hDirection,
                          globalStyles.hBetweenItems,
                          globalStyles.mb2
                        ]}
                      >
                        <View style={{ justifyContent: 'center', width: '48%' }}>
                          <Text
                            style={[
                              globalStyles.inputLabel,
                              globalStyles.inputLabelLarge,
                              globalStyles.inputLabelPrimary
                            ]}
                          >
                            {t('Country of Issuance')}
                          </Text>
                        </View>
                        <View style={{ width: '48%' }}>
                          <TextInput
                            style={[
                              globalStyles.input,
                              globalStyles.inputLight
                            ]}
                            value={props.values.passportCountry}
                            onChangeText={props.handleChange('passportCountry')}
                            keyboardType="default"
                          />
                          {props.touched.passportCountry &&
                            props.errors.passportCountry && (
                              <Text style={[globalStyles.dangerAlert]}>
                                {props.errors.passportCountry as string}
                              </Text>
                            )}
                        </View>
                      </View>
                    )}
                  </>
                )}
                {loading ? (
                  <ActivityIndicator />
                ) : (
                  <View style={{ margin: 10 }}>
                    <CustomButton
                      text={mode === 'upload' ? t('submitinfo') : t('next')}
                      onPress={props.handleSubmit}
                    />
                  </View>
                )}
              </Card>
            </>
          )}
        </Formik>
      </View>
    </KeyboardAwareScrollView>
  )
}
export default I9InformationForm
